import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '../../components/modals/confirmation';
import OverlayLoader from '../../components/overlayLoader';
import { SkeletonRows } from '../../components/skeleton';
import { getUsers, resendInvite } from '../../services/companyUsers';
import { deleteUser } from '../../services/users';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { roleListLabel } from '../../constants/labels';
import SetUserModal from './usersSet';
import Avatar from '../../components/avatar';

const PencilIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);
const MailIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 6l-10 7L2 6"/>
    </svg>
);

const StatusChip = ({ status, statusChip }) => {
    const chip = statusChip[status]
    if (!chip) return null
    return (
        <span style={{
            fontSize: '11px', fontWeight: 800, padding: '3px 9px',
            borderRadius: '999px', background: chip.bg,
            border: `1px solid ${chip.border}`, color: chip.color,
            letterSpacing: '.3px', whiteSpace: 'nowrap',
        }}>
            {chip.label}
        </span>
    )
}

const UsersPage = () => {
    const { t } = useTranslation();

    // Invitation status from the API: invited = never accepted the invitation,
    // needs-password = existing account that must choose a password, active = ok.
    const STATUS_CHIP = {
        invited: { label: t('settingsPage.invited'), bg: '#FFF7ED', border: '#FED7AA', color: '#9A3412' },
        'needs-password': { label: t('settingsPage.needsPassword'), bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
        active: { label: t('common.active'), bg: '#ECFDF5', border: '#A7F3D0', color: '#065F46' },
    };

    const [userCreateModal, setUserCreateModal] = useState(false)
    const [isShowConfirmation, setIsShowConfirmation] = useState(false)

    const [users, setUsers] = useState([])
    const [selectedUser, setSelectedUser] = useState({})

    const queryClient = useQueryClient()

    const { data, isFetching } = useQuery('users', getUsers)

    const { mutate, isLoading: isDeleting } = useMutation(deleteUser, {
        onSuccess: onMutateSuccess((result, id) => {
            queryClient.invalidateQueries('users')
        }, t('settingsPage.userDeleted'))
    })

    const { mutate: resend, isLoading: isResending, variables: resendingId } = useMutation(resendInvite, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries('users')
        }, t('settingsPage.invitationSent'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setUsers(result)
        })
    }, [data])

    const handleSetUser = (user) => {
        setSelectedUser(user)
        setUserCreateModal(true)
    }

    const handleConfirmation = (user) => {
        setSelectedUser(user)
        setIsShowConfirmation(true)
    }

    const handleDeleteUser = () => {
        mutate(selectedUser.id)
    }

    return (
        <div className="sbr">
            <ConfirmationModal display={isShowConfirmation} toggle={setIsShowConfirmation} title={t('settingsPage.deleteUser')}
                message={t('settingsPage.confirmDeleteUser', { name: selectedUser?.fullName })}
                confirmAction={handleDeleteUser}
            />
            <SetUserModal display={userCreateModal} toggle={setUserCreateModal} selectedUser={selectedUser} />

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('settingsPage.users')}</div>
                    <div className="sub">{t('settingsPage.usersAccessDesc')}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetUser()}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('settingsPage.inviteUser')}
                    </button>
                </div>
            </div>

            {/* User list */}
            <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '6px 16px', position: 'relative', minHeight: '80px' }}>
                    {users.length === 0 && isFetching ? (
                        <div style={{ padding: '16px 8px' }}>
                            <SkeletonRows rows={5} widths={['14%', '20%', '18%', '20%', '14%', '14%']} />
                        </div>
                    ) : users.length === 0 ? (
                        <div className="sbr-empty">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            {t('settingsPage.noUsers')}
                        </div>
                    ) : (
                        users.map((user, idx) => (
                            <div key={user.id ?? idx} className="row-sbr users-row">
                                <Avatar className="a" size={38} name={user.fullName} src={user?.avatarUrl} />
                                <div className="info">
                                    <div className="nm">{user.fullName || user.username}</div>
                                    <div className="sub users-contact">
                                        <span className="users-email">{user.email}</span>
                                        {user.username && <span className="users-username">{user.username}</span>}
                                    </div>
                                </div>
                                <div className="rt users-actions">
                                    {user.roles && (
                                        <span style={{
                                            fontSize: '11px', fontWeight: 800, padding: '3px 9px',
                                            borderRadius: '999px', background: 'var(--brand-50)',
                                            border: '1px solid var(--brand-100)', color: 'var(--brand-ink)',
                                            letterSpacing: '.3px', whiteSpace: 'nowrap',
                                        }}>
                                            {roleListLabel(user.roles)}
                                        </span>
                                    )}
                                    <StatusChip status={user.status} statusChip={STATUS_CHIP} />
                                    {user.status && user.status !== 'active' && (
                                        <button title={t('settingsPage.resendInvitation')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                            style={{ padding: '6px 8px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                            disabled={isResending && resendingId === user.id}
                                            onClick={() => resend(user.id)}>
                                            <MailIcon />
                                            <span style={{ fontSize: 12, fontWeight: 700 }}>
                                                {isResending && resendingId === user.id ? t('settingsPage.sendingInvite') : t('settingsPage.resendInvitation')}
                                            </span>
                                        </button>
                                    )}
                                    <div className="users-btns">
                                        <button title={t('common.edit')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                            style={{ padding: '6px 8px', color: 'var(--brand)' }}
                                            onClick={() => handleSetUser(user)}>
                                            <PencilIcon />
                                        </button>
                                        <button title={t('common.delete')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                            style={{ padding: '6px 8px', color: 'var(--bad)' }}
                                            onClick={() => handleConfirmation(user)}>
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    {((isFetching && users.length > 0) || isDeleting) && <OverlayLoader />}
                </div>
            </div>
        </div>
    );
};

export default UsersPage;
